import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  message,
  Alert,
  Space,
  Typography,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SaveOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { baseService } from "~/services/base";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

interface QuickBooksCredentials {
  id?: string;
  clientId?: string;
  redirectUri?: string;
  environment?: string;
  isActive?: boolean;
}

export const QuickBooksTab: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [credentials, setCredentials] = useState<QuickBooksCredentials | null>(
    null
  );
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    try {
      const response = await baseService.get("/api/quickbooks/credentials");
      if (response.data) {
        setCredentials(response.data);
        form.setFieldsValue({
          clientId: response.data.clientId,
          redirectUri: response.data.redirectUri,
          environment: response.data.environment || "sandbox",
        });
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error("Failed to load credentials:", error);
      }
    }
  };

  const handleSave = async (values: {
    clientId: string;
    clientSecret?: string;
    redirectUri: string;
    environment: string;
  }) => {
    setLoading(true);
    setTestResult(null);
    try {
      // If updating and clientSecret is empty, don't send it
      const payload = { ...values };
      if (credentials && !payload.clientSecret) {
        delete payload.clientSecret;
      }
      
      const response = await baseService.post(
        "/api/quickbooks/credentials",
        payload
      );
      message.success("QuickBooks credentials saved successfully!");
      setCredentials(response.data);
      await loadCredentials();
      // Clear the secret field after saving
      form.setFieldsValue({ clientSecret: undefined });
    } catch (error: any) {
      message.error(
        error.response?.data?.message ||
          "Failed to save QuickBooks credentials"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await baseService.post(
        "/api/quickbooks/credentials/test"
      );
      setTestResult({
        success: true,
        message: "Credentials are configured and accessible",
      });
      message.success("Credentials test passed!");
    } catch (error: any) {
      setTestResult({
        success: false,
        message:
          error.response?.data?.message || "Credentials test failed",
      });
      message.error("Credentials test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <Title level={4}>QuickBooks Integration</Title>
        <Paragraph className="text-gray-600">
          Configure your QuickBooks OAuth credentials to enable invoice
          synchronization. These credentials are stored securely and encrypted.
        </Paragraph>

        {credentials && (
          <Alert
            message="Credentials Configured"
            description={
              <div>
                <div>QuickBooks credentials are set up. Environment: {credentials.environment}</div>
                {credentials.redirectUri && (
                  <div style={{ marginTop: 8 }}>
                    <strong>Redirect URI:</strong>{" "}
                    <code style={{ 
                      background: '#fff', 
                      padding: '2px 6px', 
                      borderRadius: 3,
                      fontSize: '12px'
                    }}>
                      {credentials.redirectUri}
                    </code>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: 4 }}>
                      ⚠️ Make sure this exact URL is configured in your QuickBooks App settings
                    </div>
                  </div>
                )}
              </div>
            }
            type="success"
            icon={<CheckCircleOutlined />}
            className="mb-6"
            showIcon
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            environment: "sandbox",
          }}
        >
          <Form.Item
            label="Client ID"
            name="clientId"
            rules={[
              { required: true, message: "Please enter your Client ID" },
            ]}
            help="Get this from your QuickBooks App at developer.intuit.com"
          >
            <Input
              placeholder="Enter QuickBooks Client ID"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Client Secret"
            name="clientSecret"
            rules={[
              {
                required: !credentials,
                message: "Please enter your Client Secret",
              },
            ]}
            help={
              credentials
                ? "Leave blank to keep existing secret, or enter new one to update"
                : "Get this from your QuickBooks App at developer.intuit.com"
            }
          >
            <Input.Password
              placeholder={
                credentials
                  ? "Enter new Client Secret (or leave blank to keep existing)"
                  : "Enter QuickBooks Client Secret"
              }
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Redirect URI"
            name="redirectUri"
            rules={[
              { required: true, message: "Please enter your Redirect URI" },
            ]}
            help={
              <>
                Must match the redirect URI configured in your QuickBooks App.
                <br />
                <strong>Note:</strong> QuickBooks requires a real domain (not localhost).
                For local testing, use your production domain (e.g.,{" "}
                <code>https://arthandler.app/api/quickbooks/auth/callback</code>).
                The backend will redirect back to your local frontend after authentication.
              </>
            }
          >
            <Input
              placeholder="https://arthandler.app/api/quickbooks/auth/callback"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Environment"
            name="environment"
            rules={[
              { required: true, message: "Please select an environment" },
            ]}
            help="Use 'sandbox' for testing, 'production' for live QuickBooks"
          >
            <Select size="large">
              <Option value="sandbox">Sandbox (Testing)</Option>
              <Option value="production">Production (Live)</Option>
            </Select>
          </Form.Item>

          <Divider />

          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
              size="large"
            >
              Save Credentials
            </Button>

            {credentials && (
              <Button
                icon={<LinkOutlined />}
                onClick={handleTest}
                loading={testing}
                size="large"
              >
                Test Credentials
              </Button>
            )}
          </Space>

          {testResult && (
            <Alert
              message={testResult.success ? "Test Passed" : "Test Failed"}
              description={testResult.message}
              type={testResult.success ? "success" : "error"}
              icon={
                testResult.success ? (
                  <CheckCircleOutlined />
                ) : (
                  <CloseCircleOutlined />
                )
              }
              className="mt-4"
              showIcon
            />
          )}
        </Form>

        <Divider />

        <Card size="small" className="bg-blue-50">
          <Title level={5}>Setup Instructions</Title>
          <ol className="list-decimal ml-5 space-y-2 text-sm">
            <li>
              Go to{" "}
              <a
                href="https://developer.intuit.com/app/developer/myapps"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                QuickBooks Developer Portal
              </a>
            </li>
            <li>Create a new app or select an existing one</li>
            <li>Copy your Client ID and Client Secret</li>
            <li>
              Set your Redirect URI in QuickBooks to your production domain:
              <br />
              <code className="bg-white px-2 py-1 rounded text-xs block mt-1">
                https://arthandler.app/api/quickbooks/auth/callback
              </code>
              <div className="text-xs text-orange-600 mt-1 ml-0">
                ⚠️ QuickBooks doesn't accept localhost, so use your production
                domain even for local testing
              </div>
            </li>
            <li>Enter the credentials above and save</li>
            <li>
              <strong>For Local Testing:</strong> Make sure your backend's{" "}
              <code>FRONTEND_URL</code> environment variable is set to{" "}
              <code>http://localhost:5173</code> (or your local frontend URL).
              This ensures the callback redirects back to your local app.
            </li>
            <li>
              After saving, go to{" "}
              <a
                href="/billing/invoicing"
                className="text-blue-600 hover:underline"
              >
                Billing → Invoicing
              </a>{" "}
              to connect your QuickBooks account
            </li>
          </ol>
        </Card>
      </Card>
    </div>
  );
};

